import type { OfferFilters } from '@api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { applicationsApi, attributesApi, offersApi } from '@api';

// Query keys
export const offerKeys = {
  all: ['offers'] as const,
  lists: () => [...offerKeys.all, 'list'] as const,
  list: (filters?: OfferFilters) => [...offerKeys.lists(), filters] as const,
  details: () => [...offerKeys.all, 'detail'] as const,
  detail: (id: number) => [...offerKeys.details(), id] as const,
  my: (filters?: OfferFilters) => [...offerKeys.all, 'my', filters] as const,
  applications: (offerId: number, page?: number) =>
    [...offerKeys.detail(offerId), 'applications', page] as const,
};

export const attributeKeys = {
  all: ['attributes'] as const,
  list: () => [...attributeKeys.all, 'list'] as const,
};

// Offers hooks
export const useOffers = (filters?: OfferFilters) => {
  return useQuery({
    queryKey: offerKeys.list(filters),
    queryFn: () => offersApi.list(filters),
  });
};

export const useOffer = (id: number) => {
  return useQuery({
    queryKey: offerKeys.detail(id),
    queryFn: () => offersApi.get(id),
    enabled: !!id,
  });
};

export const useMyOffers = (filters?: OfferFilters) => {
  return useQuery({
    queryKey: offerKeys.my(filters),
    queryFn: () => offersApi.getMyOffers(filters),
  });
};

export const useOfferApplications = (offerId: number, page?: number) => {
  return useQuery({
    queryKey: offerKeys.applications(offerId, page),
    queryFn: () => offersApi.getApplications(offerId, page),
    enabled: !!offerId,
  });
};

// Mutations
export const useCreateOffer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: FormData) => offersApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: offerKeys.all });
    },
  });
};

export const useUpdateOffer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: FormData }) => offersApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: offerKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: offerKeys.lists() });
    },
  });
};

export const useDeleteOffer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => offersApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: offerKeys.all });
    },
  });
};

export const usePublishOffer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => offersApi.publish(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: offerKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: offerKeys.lists() });
    },
  });
};

export const useBoostOffer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => offersApi.boost(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: offerKeys.detail(id) });
    },
  });
};

// Applications
export const useCreateApplication = () => {
  return useMutation({
    mutationFn: (data: FormData) => applicationsApi.create(data),
  });
};

// Attributes
export const useAttributes = () => {
  return useQuery({
    queryKey: attributeKeys.list(),
    queryFn: () => attributesApi.list(),
    staleTime: 1000 * 60 * 30, // 30 minutes - attributes rarely change
  });
};
